import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CheckCircle2, XCircle, Flag, Bookmark, Clock, ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

function formatTime(sec) {
  if (sec < 0) sec = 0;
  const m = Math.floor(sec / 60).toString().padStart(2, "0");
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function QuizRunner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const incoming = location.state?.quiz;
  const [quiz, setQuiz] = useState(incoming || null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({}); // qid -> {selected, marked_for_review}
  const [practiceFeedback, setPracticeFeedback] = useState({}); // qid -> {correct_option, explanation}
  const [elapsed, setElapsed] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("ambiguous");
  const [reportNote, setReportNote] = useState("");
  const startedAt = useRef(Date.now());

  // if page refreshed, we can't recover questions snapshot without session store;
  // we attempt to fetch the saved session (which only exposes answers after submit).
  useEffect(() => {
    if (!quiz) {
      // No state: this likely means a refresh. Go back to setup.
      toast.error("Quiz session lost. Please start a new quiz.");
      navigate("/quiz/new", { replace: true });
    }
  }, [quiz, navigate]);

  const current = quiz?.questions?.[idx];
  const total = quiz?.questions?.length || 0;
  const answered = Object.values(answers).filter((a) => a.selected).length;
  const isMock = quiz?.mode === "mock";
  const durationSec = (quiz?.duration_minutes || 0) * 60;
  const timeLeft = durationSec > 0 ? Math.max(0, durationSec - elapsed) : null;

  // Timer tick
  useEffect(() => {
    const t = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt.current) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const submit = useCallback(async () => {
    if (submitting || !quiz) return;
    setSubmitting(true);
    try {
      const payload = {
        time_taken_seconds: Math.floor((Date.now() - startedAt.current) / 1000),
        answers: quiz.questions.map((q) => ({
          question_id: q.id,
          selected: answers[q.id]?.selected || null,
          marked_for_review: !!answers[q.id]?.marked_for_review,
        })),
      };
      await api.post(`/quiz/${id}/submit`, payload);
      navigate(`/quiz/${id}/result`, { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  }, [answers, id, navigate, quiz, submitting]);

  // Auto-submit on timeout
  useEffect(() => {
    if (isMock && durationSec > 0 && elapsed >= durationSec) {
      submit();
    }
  }, [elapsed, isMock, durationSec, submit]);

  const select = (opt) => {
    if (!current) return;
    setAnswers((a) => ({ ...a, [current.id]: { ...(a[current.id] || {}), selected: opt } }));
    if (quiz.mode === "practice" && !practiceFeedback[current.id]) {
      // Fetch answer from server (practice reveals immediately via result-like endpoint).
      // Our backend doesn't have a per-question reveal for students, so we store attempt
      // via a lightweight check: we'll fetch after submit. For practice mode we submit on last Q.
      // Instead, call a special reveal path: use /questions/:id with role=admin check (fails).
      // Simpler approach: in practice mode, after each answer the student sees whether their
      // selection matches the server-known correct via a per-question submit endpoint.
      // We implement an inline AJAX endpoint by piggy-backing on quiz state: submit a single-item
      // check via /quiz/{sid}/submit? No — it would end the session. Instead reveal from server
      // via a dedicated endpoint added below.
      api.get(`/quiz/${id}/reveal/${current.id}`).then((r) => {
        setPracticeFeedback((p) => ({ ...p, [current.id]: r.data }));
      }).catch(() => {});
    }
  };

  const toggleReview = () => {
    if (!current) return;
    setAnswers((a) => ({
      ...a,
      [current.id]: { ...(a[current.id] || {}), marked_for_review: !a[current.id]?.marked_for_review },
    }));
  };

  const toggleBookmark = async () => {
    if (!current) return;
    const isOn = bookmarkedIds.has(current.id);
    try {
      if (isOn) {
        await api.delete(`/bookmarks/${current.id}`);
        setBookmarkedIds((s) => { const n = new Set(s); n.delete(current.id); return n; });
        toast.success("Bookmark removed");
      } else {
        await api.post("/bookmarks", { question_id: current.id });
        setBookmarkedIds((s) => new Set(s).add(current.id));
        toast.success("Bookmarked");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (!quiz || !current) return null;

  const sel = answers[current.id]?.selected;
  const marked = !!answers[current.id]?.marked_for_review;
  const feedback = quiz.mode === "practice" ? practiceFeedback[current.id] : null;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[hsl(var(--brand-orange))]">
            {quiz.mode} mode
          </div>
          <h1 className="mt-1 font-display text-2xl font-bold">Question {idx + 1} of {total}</h1>
        </div>
        <div className="flex items-center gap-4">
          {timeLeft !== null && (
            <div className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-mono font-semibold ${
              timeLeft < 60 ? "bg-destructive/10 text-destructive" : "bg-secondary"
            }`} data-testid="quiz-timer">
              <Clock className="h-4 w-4" /> {formatTime(timeLeft)}
            </div>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button className="bg-[hsl(var(--brand-navy))] hover:bg-[hsl(var(--brand-navy))]/90" data-testid="submit-quiz-btn">
                Submit quiz
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Submit this quiz?</AlertDialogTitle>
                <AlertDialogDescription>
                  You have answered {answered} of {total}. Unanswered questions will be marked as unanswered.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="confirm-submit-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={submit} disabled={submitting} data-testid="confirm-submit-ok">
                  {submitting ? "Submitting..." : "Submit"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <Progress value={((idx + 1) / total) * 100} className="h-1.5" />

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_280px]">
        {/* Question card */}
        <Card className="border-border/70 card-elevated">
          <CardContent className="p-6 sm:p-8">
            <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="rounded-full bg-secondary px-2 py-0.5 font-semibold">{current.subject_name}</span>
              {current.topic_name && <span className="rounded-full bg-secondary px-2 py-0.5">{current.topic_name}</span>}
              <span className="rounded-full bg-secondary px-2 py-0.5">{current.difficulty}</span>
            </div>
            <div className="font-display text-lg font-semibold leading-relaxed sm:text-xl" data-testid="question-text">
              {current.text}
            </div>
            {current.image_url && (
              <img src={current.image_url} alt="" className="mt-5 max-h-64 rounded-xl border border-border" />
            )}

            <div className="mt-6 grid gap-3">
              {Object.keys(current.options).sort().map((k) => {
                const isSelected = sel === k;
                let state = "idle";
                if (feedback) {
                  if (k === feedback.correct_option) state = "correct";
                  else if (isSelected) state = "incorrect";
                }
                const classes = {
                  idle: isSelected
                    ? "border-[hsl(var(--brand-navy))] bg-[hsl(var(--brand-navy))]/5"
                    : "border-border hover:border-[hsl(var(--brand-navy))]/60 hover:bg-secondary",
                  correct: "border-emerald-500 bg-emerald-50",
                  incorrect: "border-destructive bg-destructive/5",
                }[state];
                const badge = {
                  idle: "bg-secondary text-foreground",
                  correct: "bg-emerald-500 text-white",
                  incorrect: "bg-destructive text-white",
                }[state];
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => !feedback && select(k)}
                    disabled={!!feedback}
                    className={`flex items-start gap-4 rounded-xl border px-5 py-4 text-left transition ${classes} ${feedback ? "cursor-default" : "cursor-pointer"}`}
                    data-testid={`option-${k}-btn`}
                  >
                    <div className={`flex h-9 w-9 flex-none items-center justify-center rounded-full text-sm font-bold ${badge}`}>
                      {state === "correct" ? <CheckCircle2 className="h-5 w-5" /> :
                       state === "incorrect" ? <XCircle className="h-5 w-5" /> : k}
                    </div>
                    <div className="flex-1 text-sm leading-relaxed sm:text-base">{current.options[k]}</div>
                  </button>
                );
              })}
            </div>

            {feedback && (
              <div className="mt-5 rounded-xl border border-border bg-secondary/50 p-4 text-sm leading-relaxed" data-testid="explanation-box">
                <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--brand-orange))]">
                  {sel === feedback.correct_option ? "Correct" : "Explanation"}
                </div>
                <div className="font-medium">Correct option: {feedback.correct_option}</div>
                <div className="mt-2 whitespace-pre-wrap">{feedback.explanation}</div>
                {current.reference && <div className="mt-2 text-xs text-muted-foreground">Ref: {current.reference}</div>}
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={toggleReview} data-testid="toggle-review-btn" className={marked ? "border-[hsl(var(--brand-orange))] text-[hsl(var(--brand-orange))]" : ""}>
                <Flag className="mr-2 h-4 w-4" /> {marked ? "Marked" : "Mark for review"}
              </Button>
              <Button variant="outline" size="sm" onClick={toggleBookmark} data-testid="toggle-bookmark-btn" className={bookmarkedIds.has(current.id) ? "border-[hsl(var(--brand-navy))] text-[hsl(var(--brand-navy))]" : ""}>
                <Bookmark className="mr-2 h-4 w-4" /> {bookmarkedIds.has(current.id) ? "Saved" : "Bookmark"}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setReportOpen(true)} data-testid="report-question-btn">
                <AlertTriangle className="mr-2 h-4 w-4" /> Report
              </Button>
              <div className="ml-auto flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0} data-testid="prev-btn">
                  <ChevronLeft className="mr-1 h-4 w-4" /> Previous
                </Button>
                <Button size="sm" onClick={() => setIdx((i) => Math.min(total - 1, i + 1))} disabled={idx === total - 1} className="bg-[hsl(var(--brand-navy))] hover:bg-[hsl(var(--brand-navy))]/90" data-testid="next-btn">
                  Next <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Navigation panel */}
        <Card className="h-fit border-border/70">
          <CardContent className="p-5">
            <div className="mb-3 flex items-center justify-between">
              <div className="font-display text-sm font-bold">Question map</div>
              <div className="text-xs text-muted-foreground">{answered}/{total}</div>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {quiz.questions.map((q, i) => {
                const a = answers[q.id];
                const isCurrent = i === idx;
                let cls = "bg-secondary text-foreground border-border";
                if (a?.marked_for_review) cls = "bg-[hsl(var(--brand-orange))]/15 border-[hsl(var(--brand-orange))] text-[hsl(var(--brand-orange))]";
                else if (a?.selected) cls = "bg-[hsl(var(--brand-navy))]/15 border-[hsl(var(--brand-navy))] text-[hsl(var(--brand-navy))]";
                if (isCurrent) cls += " ring-2 ring-[hsl(var(--brand-navy))]";
                return (
                  <button
                    key={q.id}
                    onClick={() => setIdx(i)}
                    className={`rounded-md border px-0 py-2 text-xs font-semibold ${cls}`}
                    data-testid={`qmap-${i + 1}`}
                  >
                    {i + 1}
                  </button>
                );
              })}
            </div>
            <div className="mt-5 space-y-1.5 text-xs">
              <div className="flex items-center gap-2"><span className="h-3 w-3 rounded border border-[hsl(var(--brand-navy))] bg-[hsl(var(--brand-navy))]/15" /> Answered</div>
              <div className="flex items-center gap-2"><span className="h-3 w-3 rounded border border-[hsl(var(--brand-orange))] bg-[hsl(var(--brand-orange))]/15" /> Marked for review</div>
              <div className="flex items-center gap-2"><span className="h-3 w-3 rounded border border-border bg-secondary" /> Not visited / unanswered</div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Report this question</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Why are you reporting?</div>
              <Select value={reportReason} onValueChange={setReportReason}>
                <SelectTrigger data-testid="report-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="incorrect_answer">Correct answer appears wrong</SelectItem>
                  <SelectItem value="ambiguous">Ambiguous / multiple answers could be correct</SelectItem>
                  <SelectItem value="typo">Typo / grammar error</SelectItem>
                  <SelectItem value="image_broken">Image or diagram not loading</SelectItem>
                  <SelectItem value="outdated">Outdated regulation / reference</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Textarea placeholder="Optional note (what seems wrong?)" value={reportNote} onChange={(e) => setReportNote(e.target.value)} data-testid="report-note-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportOpen(false)}>Cancel</Button>
            <Button
              className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90"
              data-testid="report-submit-btn"
              onClick={async () => {
                try {
                  await api.post("/question-reports", { question_id: current.id, reason: reportReason, note: reportNote });
                  toast.success("Thanks — we'll review this question");
                  setReportOpen(false); setReportNote(""); setReportReason("ambiguous");
                } catch (e) { toast.error("Could not submit report"); }
              }}
            >Send report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
